class GoldPriceManager {
  constructor() {
    this.API_BASE_URL = "https://n8n.exclusivejewellers.in/webhook/edb5416d-a8e7-4504-ba4a-f16c78f87e13/price-updater/";
    this.summaryContainer = document.getElementById(
      "dynamic-gold-price-summary",
    );
    this.breakupContainer = document.getElementById("price-breakup-tab");
    this.productInfo = document.querySelector("product-info");

    this.variantBreakups = [];
    this.legacyBreakup = null;

    this.init();
  }

  async init() {
    if (!this.productInfo) return;

    const productId = this.productInfo.dataset.productId;
    if (!productId) {
      console.error("Gold Price Manager: No product ID found");
      return;
    }

    try {
      this.showLoading();
      const data = await this.fetchPriceBreakup(productId);

      if (data) {
        if (data.variants && Array.isArray(data.variants)) {
          this.variantBreakups = data.variants;
          this.renderForCurrentVariant();
        } else {
          this.legacyBreakup = data;
          this.renderUI(data);
        }

        this.setupVariantListener();
      } else {
        this.showError();
      }
    } catch (error) {
      console.error("Gold Price Manager Error:", error);
      this.showError();
    }
  }

  async fetchPriceBreakup(productId) {
    const response = await fetch(`${this.API_BASE_URL}${productId}`);
    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }
    return await response.json();
  }

  getCurrentVariantId() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantFromUrl = urlParams.get("variant");
    if (variantFromUrl) {
      return `gid://shopify/ProductVariant/${variantFromUrl}`;
    }

    const variantInput = document.querySelector('input[name="id"]');
    if (variantInput && variantInput.value) {
      return `gid://shopify/ProductVariant/${variantInput.value}`;
    }

    if (this.productInfo && this.productInfo.dataset.variantId) {
      return `gid://shopify/ProductVariant/${this.productInfo.dataset.variantId}`;
    }

    const selectedVariant = document.querySelector(".product-variant-id");
    if (selectedVariant && selectedVariant.value) {
      return `gid://shopify/ProductVariant/${selectedVariant.value}`;
    }

    return null;
  }

  getBreakupForCurrentVariant() {
    const currentVariantId = this.getCurrentVariantId();

    if (this.variantBreakups.length === 0) {
      return this.legacyBreakup;
    }

    if (currentVariantId) {
      const match = this.variantBreakups.find(
        (v) => v.variantId === currentVariantId,
      );
      if (match) return match;
    }

    return this.variantBreakups[0];
  }

  renderForCurrentVariant() {
    this.showSkeleton();

    requestAnimationFrame(() => {
      const breakup = this.getBreakupForCurrentVariant();
      if (breakup) {
        this.renderUI(breakup);
      } else {
        this.showError();
      }
    });
  }

  showSkeleton() {
    const skeletonStyles = `
      <style>
        .skeleton-loader {
          animation: skeleton-pulse 1.5s ease-in-out infinite;
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          border-radius: 4px;
        }
        @keyframes skeleton-pulse {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .skeleton-price { height: 32px; width: 140px; margin-bottom: 8px; }
        .skeleton-mrp { height: 18px; width: 200px; }
        .skeleton-row { height: 20px; width: 100%; margin: 12px 0; }
      </style>
    `;

    if (this.summaryContainer) {
      this.summaryContainer.innerHTML = `
        ${skeletonStyles}
        <div class="summary-price-container">
          <div class="skeleton-loader skeleton-price"></div>
          <div class="skeleton-loader skeleton-mrp"></div>
        </div>
      `;
    }

    if (this.breakupContainer) {
      this.breakupContainer.innerHTML = `
        ${skeletonStyles}
        <div class="breakup-table">
          <div class="skeleton-loader skeleton-row"></div>
          <div class="skeleton-loader skeleton-row"></div>
          <div class="skeleton-loader skeleton-row"></div>
          <div class="skeleton-loader skeleton-row"></div>
        </div>
      `;
    }
  }

  setupVariantListener() {
    window.addEventListener("popstate", () => {
      this.renderForCurrentVariant();
    });

    const variantInput = document.querySelector('input[name="id"]');
    if (variantInput) {
      const observer = new MutationObserver(() => {
        this.renderForCurrentVariant();
      });
      observer.observe(variantInput, {
        attributes: true,
        attributeFilter: ["value"],
      });

      variantInput.addEventListener("change", () => {
        this.renderForCurrentVariant();
      });
    }

    document.addEventListener("variant:changed", () => {
      this.renderForCurrentVariant();
    });

    const variantPickers = document.querySelectorAll(
      '.product-form__input input[type="radio"], .variant-input',
    );
    variantPickers.forEach((picker) => {
      picker.addEventListener("change", () => {
        setTimeout(() => this.renderForCurrentVariant(), 100);
      });
    });

    const variantOptions = document.querySelectorAll(
      "[data-option-value], .swatch-input, .variant-option",
    );
    variantOptions.forEach((option) => {
      option.addEventListener("click", () => {
        setTimeout(() => this.renderForCurrentVariant(), 150);
      });
    });
  }

  showLoading() {
    this.showSkeleton();
  }

  showError() {
    const errorHtml = `
      <div class="gold-price-error" style="color: #888; font-size: 0.9rem; padding: 10px 0;">
        <span>Unable to fetch price details</span>
      </div>
    `;
    if (this.summaryContainer) this.summaryContainer.innerHTML = errorHtml;
    if (this.breakupContainer) this.breakupContainer.innerHTML = errorHtml;
  }

  formatCurrency(amount) {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  renderUI(data) {
    this.renderSummary(data);
    this.renderBreakupTable(data);
  }

  renderSummary(data) {
    if (!this.summaryContainer) return;

    const mrp = data.mrp || data.finalPrice * 1.1;

    this.summaryContainer.innerHTML = `
      <div class="summary-price-container">
         <span class="summary-final-price">${this.formatCurrency(data.finalPrice)}</span>
         <div style="display:flex; align-items:center;">
             <span class="summary-mrp">${this.formatCurrency(mrp)}</span>
             <span class="summary-tax-note">(MRP Inclusive of all taxes)</span>
         </div>
      </div>
    `;
  }

  renderBreakupTable(data) {
    if (!this.breakupContainer) return;

    const styles = `
      <style>
        .breakup-table {
          width: 100%;
          border-collapse: collapse;
          font-family: inherit;
        }
        .breakup-row {
          display: flex;
          justify-content: space-between;
          padding: 12px 0;
          border-bottom: 1px solid #eee;
          align-items: center;
        }
        .breakup-row:last-child {
          border-bottom: none;
          font-weight: 700;
          color: inherit;
          margin-top: 10px;
          border-top: 1px solid #ffe6f0;
          padding-top: 15px;
        }
        .breakup-label {
          color: inherit;
        }
        .breakup-subtext {
          font-weight: 400;
          opacity: 0.8;
          margin-left: 5px;
        }
        .breakup-value {
          color: inherit;
          font-weight: 600;
        }
        .discount-badge {
            background: #fff0f5;
            color: #800040;
            padding: 2px 6px;
            border-radius: 4px;
            margin-left: 8px;
            font-weight: 600;
        }
      </style>
    `;

    const goldRate =
      data.goldRate || (data.weight > 0 ? data.goldValue / data.weight : 0);

    const goldRow = `
      <div class="breakup-row">
        <span class="breakup-label">${data.karat} ${data.goldColor || "Yellow"} Gold <span class="breakup-subtext">${this.formatCurrency(goldRate)}/g</span></span>
        <span class="breakup-value">${this.formatCurrency(data.goldValue)}</span>
      </div>
    `;

    let stoneRow = "";
    if (data.stoneCharges > 0) {
      stoneRow = `
        <div class="breakup-row">
            <span class="breakup-label">Diamond / Stone Charges</span>
            <span class="breakup-value">${this.formatCurrency(data.stoneCharges)}</span>
        </div>`;
    }

    const makingChargesRow = `
      <div class="breakup-row">
        <span class="breakup-label">Making Charge</span>
        <span class="breakup-value">${this.formatCurrency(data.makingCharges)}</span>
      </div>
    `;

    const taxRow = `
      <div class="breakup-row">
        <span class="breakup-label">TAX <span class="discount-badge">3% GST</span></span>
        <span class="breakup-value">${this.formatCurrency(data.gst)}</span>
      </div>
    `;

    const totalRow = `
      <div class="breakup-row">
        <span class="breakup-label">Grand Total</span>
        <span class="breakup-value">${this.formatCurrency(data.finalPrice)}</span>
      </div>
    `;

    this.breakupContainer.innerHTML = `
      ${styles}
      <div class="breakup-table">
        ${goldRow}
        ${stoneRow}
        ${makingChargesRow}
        ${taxRow}
        ${totalRow}
      </div>
    `;
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => new GoldPriceManager());
} else {
  new GoldPriceManager();
}
